import React, { FC, useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import styled from '@emotion/styled';
import { Box, Button, Input, SearchInputWrapper } from '~Styles';
import { useI18n, useDebounce } from '~Hooks';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Heading from '~ComponentsImproved/Typography/Heading';
import Modal from '~ComponentsImproved/Modal';
import { ImpulseSpinner } from 'react-spinners-kit';
import OptionRow from './OptionRow';

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  background: var(--colorBlueGray1200);
  border-radius: 8px;
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.2);
  width: 520px;
  max-width: calc(100% - 64px);
  height: 100%;
  max-height: 100%;
  overflow: hidden;
`;

/** A single selectable option */
export interface SearchSelectOption {
  /** Required unique id, passed back to `onSelect` */
  id: string;
  /** Required primary text shown on the left and used for searching */
  label: string;
  /** Optional icon src shown to the left of the label */
  icon?: string;
  /** Optional secondary text shown below the label */
  description?: string;
  /** Optional extra info shown to the right of the label (e.g. discount rate "20%") */
  detail?: string;
  /** Optional second extra info shown to the right of the label (e.g. discount type) */
  secondaryDetail?: string;
  /** Renders the label in italics, e.g. for a "None" placeholder option */
  italic?: boolean;
  /** Greyed out and not selectable */
  disabled?: boolean;
}

interface Props {
  /** The options to search & select from */
  options: SearchSelectOption[];
  /** Called with the selected option's id when an option is clicked */
  onSelect: (id: string) => void;
  /** The id of the currently selected option, highlighted in the list */
  selectedId?: string;
  /** Called when the user presses the "Clear" button (only shown when selectedId is set) */
  onClear?: () => void;
  /** Called when the modal is dismissed (backdrop tap) without a selection */
  onClose?: () => void;
  /** Optional heading shown above the search input */
  title?: string;
  /** Placeholder shown in the search input */
  placeholder?: string;
  /** Automatically focus the search input on mount (default true) */
  autoFocus?: boolean;
  /** Called with the debounced search term so the caller can search remotely */
  onSearchChange?: (term: string) => void;
  /** Shows a spinner instead of the list while a remote search is in flight */
  isSearchLoading?: boolean;
}

const OptionList = styled(Box)`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
`;

const NotFoundBox = styled(Box)`
  text-align: center;
`;

const ContentColumn = styled(Box)`
  flex: 1 1 auto;
  min-height: 0;
`;

const ClearButton = styled(Button)`
  max-width: 64px;
`;

const SearchSelect: FC<Props> = ({
  options,
  onSelect,
  selectedId,
  onClear,
  onClose,
  title,
  placeholder,
  autoFocus = true,
  onSearchChange,
  isSearchLoading = false,
}) => {
  const { i18n } = useI18n();
  const isHandy = window.innerWidth <= 600;

  const [searchTerm, setSearchTerm] = useState<string>('');

  /** Hidden only when there are few options and nothing typed, so a search with no hits keeps its input */
  const showSearchInput = searchTerm !== '' || options.length >= 8;

  const debouncedSearchTerm = useDebounce(searchTerm, 200);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const clearSearch = useCallback(() => setSearchTerm(''), []);

  /** Latest callback kept in a ref so the effect below never re-subscribes on identity changes */
  const onSearchChangeRef = useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;

  useEffect(() => {
    onSearchChangeRef.current?.(debouncedSearchTerm.trim());
  }, [debouncedSearchTerm]);

  /** Filtered once per term/options change, not on every render */
  const matchedOptions = useMemo(() => {
    const term = debouncedSearchTerm.trim().toLowerCase();
    return term ? options.filter((option) => option.label.toLowerCase().includes(term)) : options;
  }, [options, debouncedSearchTerm]);

  const listRef = useRef<HTMLDivElement>(null);
  /** Pending animation frame so rapid scroll events collapse into one style write */
  const maskFrame = useRef(0);

  const updateMask = useCallback(() => {
    if (maskFrame.current) return;

    maskFrame.current = requestAnimationFrame(() => {
      maskFrame.current = 0;
      const el = listRef.current;
      if (!el) return;

      /** Fade region grows with scroll distance so it eases in instead of popping */
      const FADE = 40;
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      const topFade = Math.min(Math.max(el.scrollTop, 0), FADE);
      const bottomFade = Math.min(Math.max(remaining, 0), FADE);
      const mask = `linear-gradient(to bottom, rgba(0, 0, 0, 0) 0, rgba(0, 0, 0, 1) ${topFade}px, rgba(0, 0, 0, 1) calc(100% - ${bottomFade}px), rgba(0, 0, 0, 0) 100%)`;

      el.style.setProperty('mask-image', mask);
      el.style.setProperty('-webkit-mask-image', mask);
    });
  }, []);

  useEffect(() => {
    updateMask();
  }, [updateMask, matchedOptions.length]);

  useEffect(() => () => cancelAnimationFrame(maskFrame.current), []);

  /** Stable identity so memoised rows are not re-rendered when SearchSelect re-renders */
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const selectOption = useCallback((id: string) => onSelectRef.current(id), []);

  return (
    <Modal isShowing={true} hide={onClose}>
      <ModalContent>
        <ContentColumn
          display="flex"
          flexDirection="column"
          fluid
          gap={isHandy ? 'small' : 'large'}>
          {title && (
            <Box display="flex" justifyContent="space-between" alignItems="center" fluid>
              <Heading level={3} color="var(--colorWhite)">
                {title}
              </Heading>

              {selectedId && onClear && (
                <ClearButton fluid onClick={onClear}>
                  {i18n('General.Clear')}
                </ClearButton>
              )}
            </Box>
          )}

          {showSearchInput && (
            <SearchInputWrapper fluid value={searchTerm} onClear={clearSearch}>
              <Input
                id="search-select"
                name="search-select"
                placeholder={placeholder || i18n('Settings.Search.Placeholder')}
                value={searchTerm}
                onChange={handleSearchChange}
                dark
                padding="small"
                isSearch
                autoFocus={autoFocus}
                fluid
              />
            </SearchInputWrapper>
          )}

          <OptionList
            ref={listRef}
            onScroll={updateMask}
            display="flex"
            flexDirection="column"
            gap="xs"
            fluid>
            {isSearchLoading ? (
              <NotFoundBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                padding="large">
                <ImpulseSpinner size={48} />
              </NotFoundBox>
            ) : matchedOptions.length === 0 ? (
              <NotFoundBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                padding="large">
                <Paragraph level={isHandy ? 4 : 3} color="var(--colorBlueGray200)">
                  {i18n('CustomerForCheckout.NotFound')}
                </Paragraph>
              </NotFoundBox>
            ) : (
              <>
                {matchedOptions.map((option) => (
                  <OptionRow
                    key={option.id}
                    option={option}
                    isSelected={selectedId === option.id}
                    isHandy={isHandy}
                    onSelect={selectOption}
                  />
                ))}
              </>
            )}
          </OptionList>
        </ContentColumn>
      </ModalContent>
    </Modal>
  );
};

export default memo(SearchSelect);
